import { ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { FinanceEntry, EXPENSE_CATEGORIES } from './types';

const FREQUENCY_OPTIONS = [
  { id: 'once', label: 'Engångskostnad' },
  { id: 'monthly', label: 'Månadsvis' },
  { id: 'quarterly', label: 'Kvartalsvis' },
  { id: 'yearly', label: 'Årsvis' }
];

interface ExpenseFormData {
  category: string;
  amount: string;
  date: string;
  vendor: string;
  description: string;
  is_recurring: boolean;
  frequency: string;
}

interface ExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedExpense: FinanceEntry | null;
  form: ExpenseFormData;
  onFormChange: (form: ExpenseFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export function ExpenseDialog({
  open,
  onOpenChange,
  selectedExpense,
  form,
  onFormChange,
  onSubmit
}: ExpenseDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {selectedExpense ? 'Redigera utgift' : 'Lägg till utgift'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Kategori</Label>
            <Select 
              value={form.category} 
              onValueChange={(v) => onFormChange({ ...form, category: v })}
            >
              <SelectTrigger className="mt-1" data-testid="expense-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPENSE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <div className="flex items-center gap-2">
                      <cat.icon size={16} />
                      {cat.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Belopp (kr)</Label>
              <Input
                type="number"
                required
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => onFormChange({ ...form, amount: e.target.value })}
                className="mt-1"
                placeholder="0"
                data-testid="expense-amount-input"
              />
            </div>
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                required
                value={form.date}
                onChange={(e) => onFormChange({ ...form, date: e.target.value })}
                className="mt-1"
                data-testid="expense-date-input"
              />
            </div>
          </div>
          
          <div>
            <Label>Leverantör (valfritt)</Label>
            <Input
              value={form.vendor}
              onChange={(e) => onFormChange({ ...form, vendor: e.target.value })}
              className="mt-1"
              placeholder="t.ex. Vattenfall"
              data-testid="expense-vendor-input"
            />
          </div>
          
          <div>
            <Label>Beskrivning (valfritt)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => onFormChange({ ...form, description: e.target.value })}
              className="mt-1"
              rows={2}
              placeholder="Extra information..."
              data-testid="expense-description-input"
            />
          </div>
          
          {/* Recurring toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <ArrowsClockwise size={20} className="text-blue-600" />
              <div>
                <div className="font-medium text-slate-900 text-sm">Återkommande</div>
                <div className="text-xs text-slate-500">Utgiften upprepas regelbundet</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onFormChange({ 
                ...form, 
                is_recurring: !form.is_recurring,
                frequency: !form.is_recurring ? 'monthly' : 'once'
              })}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                form.is_recurring ? 'bg-blue-500' : 'bg-slate-300'
              }`}
              data-testid="expense-recurring-toggle"
            >
              <span 
                className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  form.is_recurring ? 'translate-x-7' : 'translate-x-1'
                }`} 
              />
            </button>
          </div>
          
          {form.is_recurring && (
            <div>
              <Label>Frekvens</Label>
              <Select 
                value={form.frequency} 
                onValueChange={(v) => onFormChange({ ...form, frequency: v })}
              >
                <SelectTrigger className="mt-1" data-testid="expense-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.filter(f => f.id !== 'once').map(freq => (
                    <SelectItem key={freq.id} value={freq.id}>{freq.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" data-testid="expense-save-button">
              {selectedExpense ? 'Uppdatera' : 'Lägg till'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
